<template>
  <component
    :is="to ? 'router-link' : 'button'"
    @click="$emit('click', $event)"
    v-bind="{
      ...$attrs,
      to,
      type,
      form,
      disabled: disabled || undefined,
    }"
    class="app-button"
    :class="{
      basic,
      error,
      success,
      transparent,
      inverted,
      outline,
      primary,
      secondary,
      tertiary,
      fluid,
      small,
      muted,
      icon,
      'no-shadow': noShadow,
      'light-rounded': lightRounded,
      'space-between': spaceBetween,
      'full-width': fullWidth,
    }"
  >
    <Loading v-if="loading" class="loading" />

    <slot v-else />
  </component>
</template>

<script>
import Loading from '@/components/Loading.vue'

export default {
  name: 'AppButton',

  emits: ['click'],

  components: {
    Loading,
  },

  props: {
    to: {
      type: Object,
      required: false,
    },

    lightRounded: {
      type: Boolean,
      default: false,
    },

    noShadow: {
      type: Boolean,
      default: false,
    },

    muted: {
      type: Boolean,
      default: false,
    },

    small: {
      type: Boolean,
      default: false,
    },

    primary: {
      type: Boolean,
      default: false,
    },

    secondary: {
      type: Boolean,
      default: false,
    },

    tertiary: {
      type: Boolean,
      default: false,
    },

    basic: {
      type: Boolean,
      default: false,
    },

    success: {
      type: Boolean,
      default: false,
    },

    error: {
      type: Boolean,
      default: false,
    },

    outline: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    fullWidth: {
      type: Boolean,
      default: false,
    },

    transparent: {
      type: Boolean,
      default: false,
    },

    inverted: {
      type: Boolean,
      default: false,
    },

    spaceBetween: {
      type: Boolean,
      default: false,
    },

    fluid: {
      type: Boolean,
      default: false,
    },

    icon: {
      type: Boolean,
      default: false,
    },

    type: {
      type: String,
      default: 'button',
    },

    form: {
      type: String,
      default: undefined,
    },
  },
}
</script>

<style lang="scss" scoped>
@use 'sass:math';
@use 'sass:color';

@use '@/styles/settings.scss' as *;
@use '@/styles/functions.scss' as *;

a.app-button {
  text-decoration: none;
}
.app-button {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0;
  line-height: 1;
  font-size: 1rem;
  border: 1px solid transparent;
  border-radius: 1rem;
  outline: 0;
  position: relative;
  background-color: #e5e3e3;
  color: currentColor;
  min-height: 2.8125rem;
  padding: 0.5rem 0.75rem;
  justify-content: center;
  align-items: center;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);

  &.no-shadow {
    box-shadow: none;
  }

  &.muted {
    background-color: var(--muted-color, rgba(0, 0, 0, 0.25));
    color: var(--muted-text-color, #000);
  }

  &.primary {
    background-color: var(--primary-color);
    color: var(--primary-text-color, #fff);
  }

  &.secondary {
    background-color: var(--secondary-color);
    color: var(--secondary-text-color, #fff);
  }

  &.tertiary {
    background-color: var(--tertiary-color);
    color: var(--tertiary-text-color, #000);
  }

  &.success {
    background-color: var(--success-color, #8ca449);
    color: var(--success-text-color, #fff);
  }

  &.light-rounded {
    border-radius: #{math.div($defaultBorderRadius, 3)};
  }

  &.outline {
    font-weight: normal;
    background-color: transparent;
    border: 1px solid currentColor;

    &.primary {
      color: var(--primary-color);
    }

    &.secondary {
      color: var(--secondary-color);
    }

    &.tertiary {
      color: var(--tertiary-color);
    }
  }

  &.small {
    font-size: 75%;
  }

  &.transparent {
    background-color: rgba(0, 0, 0, 0.25);
    color: #fff;

    &.inverted {
      background-color: rgba(255, 255, 255, 0.25);
      color: #000;
    }
  }

  &.space-between {
    justify-content: space-between;
  }

  &.error {
    $color: color.adjust($errorColor, $lightness: 10%);
    background-color: $color;
    color: #fff;
  }

  &.clean {
    background: none;
    border: 0;
    box-shadow: none;

    &.primary {
      color: var(--primary-color);
    }
  }

  &.fluid {
    flex: auto;
  }

  &.icon {
    font-size: 1.5rem;

    &:not(.transparent) {
      background-color: #f6f6f6;
    }

    > div {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &.full-width {
    width: 100%;
  }

  &[disabled] {
    opacity: 0.5;
    pointer-events: none;
  }

  > .loading {
    font-size: 0.75em;
  }
}
</style>
